/* eslint-disable camelcase */
import { client } from "@johnpaul/jp-api-client";
import { useMutation } from "@tanstack/react-query";
import {
  getClientToken,
  getHashParams,
  hashPassword,
} from "@companion-core/shared/app/Utils/apiClient";
import dayjs from "dayjs";
import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";
import { IdentityCriteria } from "@companion-core/shared/app/Interfaces/member";

interface RegisterRequestParams {
  payload: Record<string, unknown>;
  shouldEncryptData?: boolean;
}

interface RegisterEmailRequestParams {
  payload: Record<string, unknown>;
  memberId: string;
}

interface ActivateAccountParams {
  memberData: APIMemberData;
  activationFormData: Record<string, unknown>;
  identityCriteriaList: IdentityCriteria[];
  platform: string;
}

export const useRegisterRequest = () => {
  return useMutation({
    mutationFn: async ({ payload, shouldEncryptData = false }: RegisterRequestParams) => {
      const token = await getClientToken();
      return client.members.v2.register.checkEligibility(payload, token, shouldEncryptData);
    },
    retry: false,
    gcTime: 0,
  });
};

export const useRegisterUpdateEmail = () => {
  return useMutation({
    mutationFn: async ({ payload, memberId }: RegisterEmailRequestParams) => {
      const token = await getClientToken();
      return client.members.v2.register.updateMemberEmail(payload, memberId, token);
    },
    retry: false,
    gcTime: 0,
  });
};

export const useSendRegisterVerificationEmail = () => {
  return useMutation({
    mutationFn: async (memberId: string) =>
      await client.auth.v2.sendRegisterVerificationEmail(memberId),
    retry: false,
    gcTime: 0,
  });
};

export const useActivateAccount = () => {
  return useMutation({
    mutationFn: async ({
      memberData,
      activationFormData,
      identityCriteriaList,
      platform,
    }: ActivateAccountParams) => {
      const token = await getClientToken();
      const { hash_len, m, p, salt, t } = await getHashParams({
        username: memberData.username ?? undefined,
        token: token,
      });

      // Build activation payload
      const activationPayload: Record<string, unknown> = {
        ...activationFormData,
        term_of_use_accepted: true,
        registration_method: platform,
        registration_date: dayjs().format("YYYY-MM-DD"),
        marketing_email: activationFormData.marketing_email ? "opt-in" : null,
        marketing_sms: activationFormData.marketing_sms ? "opt-in" : null,
        id: memberData.id,
      };

      // Add identity criteria to the activation payload
      identityCriteriaList
        .filter((identityCriteria) => identityCriteria.editable === true)
        .forEach((identityCriteria) => {
          activationPayload[identityCriteria.criteria] = memberData[identityCriteria.criteria];
        });

      // Add encrypted web_password to the activation payload
      activationPayload.web_password = await hashPassword(
        activationPayload.web_password as string,
        salt,
        t,
        m,
        p,
        hash_len
      );

      activationPayload.web_secret_answer = activationPayload.web_secret_answer
        ? await hashPassword(activationPayload.web_secret_answer as string, salt, t, m, p, hash_len)
        : null;

      return await client.members.v2.register.activateAccount(token, activationPayload);
    },
    retry: false,
    gcTime: 0,
  });
};

export const useSignUp = () => {
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const token = await getClientToken();
      return client.members.v2.register.signUp(token, payload);
    },
    retry: false,
    gcTime: 0,
  });
};
